import {
  Button,
  Flex,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import {
  equipmentParametersByEquipmentID,
  listCompanies,
  listEquipmentTypes,
} from "graphql/queries";
import {
  updateEquipmentParameter,
  updateEquipmentType,
} from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { EquipmentTypeCreateForm, EquipmentTypeUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import EquipmentTypeParameters from "./parameters/EquipmentTypeParameters";
import EquipmentTypeDocuments from "./documents/EquipmentTypeDocuments";

const EquipmentTypes = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete, canReadParams, canReadDocs] =
    usePermissions([
      "equipment-type:create",
      "equipment-type:update",
      "equipment-type:delete",
      "equipment-type:read-param",
      "equipment-type:read-doc",
    ]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentTypesLoading, setEquipmentTypesLoading] = useState(false);
  const [equipmentTypeDetails, setEquipmentTypeDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentTypeId: null,
    equipmentTypeName: null,
  });
  const [modalEquipmentType, setModalEquipmentType] = useState({
    visible: false,
    equipmentTypeId: null,
  });
  const [modalParams, setModalParams] = useState({
    visible: false,
    equipmentTypeId: null,
  });
  const [modalDocs, setModalDocs] = useState({
    visible: false,
    equipmentTypeId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      equipmentTypes.map((item) => ({
        ...item,
        companiesNames: item.companiesNames || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                equipmentTypeDetails?.id === item.id
                  ? strings.equipmentTypes.hide
                  : strings.equipmentTypes.show
              }
              icon={
                equipmentTypeDetails?.id === item.id
                  ? "VisibilityOff"
                  : "Visibility"
              }
              color="primary"
              onClick={() => {
                equipmentTypeDetails?.id === item.id
                  ? setEquipmentTypeDetails(null)
                  : setEquipmentTypeDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.equipmentTypes.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canReadParams && (
              <ActionIcon
                name={strings.equipmentTypes.params}
                icon="Tune"
                color="primary"
                onClick={() => {
                  setModalParams({
                    visible: true,
                    equipmentTypeId: item.id,
                    equipmentTypeName: item.name,
                  });
                }}
              />
            )}
            {canReadDocs && (
              <ActionIcon
                name={strings.equipmentTypes.docs}
                icon="Folder"
                color="primary"
                onClick={() => {
                  setModalDocs({
                    visible: true,
                    equipmentTypeId: item.id,
                    equipmentTypeName: item.name,
                  });
                }}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.equipmentTypes.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    equipmentTypeId: item.id,
                    equipmentTypeName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipmentTypes, equipmentTypeDetails?.id]
  );

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchEquipmentTypes(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalEquipmentType({ visible: true, action, equipmentTypeId: id });
  const onSuccessEquipmentType = () => {
    setModalEquipmentType({ visible: false });
    fetchEquipmentTypes();
  };

  async function fetchCompaniesNames(ids) {
    try {
      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          filter: {
            or: ids.map((id) => ({ id: { eq: id } })),
          },
        })
      );
      const companies = companiesData.data.listCompanies?.items || [];
      return companies?.map((c) => c.name)?.join(", ") || "";
    } catch (err) {
      console.log("Error fetchCompaniesNames", err);
      return "";
    }
  }

  const getNamesByEquipmentTypes = (list) => {
    list?.forEach(async (item) => {
      const companiesNames = await fetchCompaniesNames(item.companies);

      setEquipmentTypes((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companiesNames,
        };

        return updatedList;
      });
    });
  };

  async function fetchEquipmentTypes(keywords) {
    let objFilter = {
      deletedAt: { attributeExists: false },
    };
    try {
      setEquipmentTypesLoading(true);

      if (keywords) {
        objFilter = {
          ...objFilter,
          and: keywords.map((key) => ({
            or: [
              {
                name: { contains: key },
              },
              {
                description: { contains: key },
              },
            ],
          })),
        };
      }

      if (!!searchBy.company.id) {
        objFilter.and = [
          ...(objFilter.and || []),
          {
            companies: {
              contains: searchBy.company.id,
            },
          },
        ];
      } else if (!!me.companyID || me.managedCompanies.length > 0) {
        objFilter.and = [
          ...(objFilter.and || []),
          {
            or: [
              {
                companies: {
                  contains: me.companyID,
                },
              },
              ...me.managedCompanies.map((managed) => ({
                companies: {
                  contains: managed,
                },
              })),
            ],
          },
        ];
      }

      const equipmentTypesData = await API.graphql(
        graphqlOperation(listEquipmentTypes, {
          filter: objFilter,
        })
      );
      const equipmentTypesItems =
        equipmentTypesData.data.listEquipmentTypes.items;
      setEquipmentTypes(equipmentTypesItems);
      getNamesByEquipmentTypes(equipmentTypesItems);
      setEquipmentTypesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setEquipmentTypesLoading(false);
    }
  }

  async function equipmentTypeParametersDelete(equipmentTypeID) {
    try {
      const results = await API.graphql(
        graphqlOperation(equipmentParametersByEquipmentID, {
          equipmentID: equipmentTypeID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.equipmentParametersByEquipmentID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateEquipmentParameter, {
              input: { id: res.id, deletedAt: new Date().toISOString() },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  async function equipmentTypeDelete(id) {
    try {
      await equipmentTypeParametersDelete(id);

      const result = await API.graphql(
        graphqlOperation(updateEquipmentType, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateEquipmentType.id;
      setEquipmentTypes(equipmentTypes.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        equipmentTypeId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchEquipmentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companiesNames",
      label: strings.table.headers.companiesNames,
      _style: { width: "40%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "40%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <Flex justifyContent="space-between">
        <span className="fs-3">{strings.title}</span>
        <Flex style={styles.buttonAdd}>
          {canCreate && (
            <>
              <Button
                children={strings.equipmentTypes.addEquipmentType}
                variation="primary"
                onClick={() => openModal("create")}
              />
            </>
          )}
        </Flex>
      </Flex>
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            onlyCompany
            searchBy={searchBy}
            setSearchBy={setSearchBy}
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {equipmentTypesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
          </>
        )}
        <CCollapse
          visible={!!equipmentTypeDetails?.id}
          style={styles.collapseCard}
        >
          <CCard key={equipmentTypeDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{equipmentTypeDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {equipmentTypeDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(equipmentTypeDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.equipmentTypeName,
        })}
        onCancel={() =>
          setModalDetails({ visible: false, equipmentTypeId: null })
        }
        onConfirm={() => equipmentTypeDelete(modalDetails.equipmentTypeId)}
      />
      {modalEquipmentType.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalEquipmentType.visible}
          onClose={() => setModalEquipmentType({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalEquipmentType.action === "create"
                ? strings.modalEquipmentType.addTitle
                : strings.modalEquipmentType.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalEquipmentType.action === "create" ? (
                <EquipmentTypeCreateForm onSuccess={onSuccessEquipmentType} />
              ) : (
                <EquipmentTypeUpdateForm
                  equipmentTypeId={modalEquipmentType.equipmentTypeId}
                  onSuccess={onSuccessEquipmentType}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {modalParams.visible && (
        <EquipmentTypeParameters
          equipmentTypeID={modalParams.equipmentTypeId}
          equipmentTypeName={modalParams.equipmentTypeName}
          visible={modalParams.visible}
          onClose={() => setModalParams({ visible: false })}
        />
      )}
      {modalDocs.visible && (
        <EquipmentTypeDocuments
          equipmentTypeID={modalDocs.equipmentTypeId}
          equipmentTypeName={modalDocs.equipmentTypeName}
          visible={modalDocs.visible}
          onClose={() => setModalDocs({ visible: false })}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Tipos de Equipo",
    successDeleted: "Tipo de Equipo eliminado con éxito",
    equipmentTypes: {
      addEquipmentType: "Agregar Tipo de Equipo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
      docs: "Documentos",
    },
    responses: {
      errorFetch: "Error al consultar tipo de equipo",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    table: {
      headers: {
        name: "Nombre",
        companiesNames: "Empresas",
        actions: "Acciones",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Tipo de Equipo",
      description: "Descripción",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el tipo de equipo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalEquipmentType: {
      addTitle: "Agregar Tipo de Equipo",
      updateTitle: "Actualizar Tipo de Equipo",
    },
  },
});

const styles = {
  buttonAdd: { justifyContent: "end", flexWrap: "wrap" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default EquipmentTypes;
